import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { useIsMobile } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";

// @dnd-kit/modifiers is not a direct dependency of chat-bridge. The sidebar
// needs only this one small modifier, so keep it local rather than adding a
// package solely to zero the horizontal transform.
const restrictToVerticalAxis: Modifier = ({ transform }) => ({
  ...transform,
  x: 0,
});

/** Shared drag-and-drop wiring for the sidebar's pinned collections. */
export interface SidebarPinSortableProps {
  ids: string[];
  onReorder: (orderedIds: string[]) => void;
  children: ReactNode;
  className?: string;
  "aria-labelledby"?: string;
  testId?: string;
}

/**
 * Vertical drag-and-drop list for pinned sidebar rows. Reordering is purely
 * presentational here: the owner persists the new order (or reports a failed
 * save) through `onReorder`.
 */
export function SidebarPinSortable({
  ids,
  onReorder,
  children,
  className,
  "aria-labelledby": ariaLabelledBy,
  testId,
}: SidebarPinSortableProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    const next = [...ids];
    next.splice(oldIndex, 1);
    next.splice(newIndex, 0, String(active.id));
    onReorder(next);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div
          className={cn("min-w-0 overflow-x-hidden", className)}
          aria-labelledby={ariaLabelledBy}
          data-testid={testId}
        >
          {children}
        </div>
      </SortableContext>
    </DndContext>
  );
}

export interface SidebarPinSortableRowProps {
  id: string;
  /** Row content rendered after the drag handle (label, chevron, …). */
  children: ReactNode;
  /** Row actions rendered at the trailing edge (the add button). */
  actions?: ReactNode;
  /** Extra classes for the desktop actions overlay. */
  actionsOverlayClassName?: string;
  /** Content rendered below the sortable row (for example, expanded tasks). */
  below?: ReactNode;
  /** Extra classes for the row container (e.g. active-row highlighting). */
  className?: string;
  /** Test id for the row container, without the drag handle. */
  testId?: string;
}

/**
 * One sortable pinned row. The drag handle sits before the label and is the
 * only drag activator so clicks on the row keep their normal behaviour. On
 * desktop the actions float over the row; touch-sized layouts retain their
 * in-flow controls.
 */
export function SidebarPinSortableRow({
  id,
  children,
  actions,
  actionsOverlayClassName,
  below,
  className,
  testId,
}: SidebarPinSortableRowProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const dragHandle = (
    <button
      type="button"
      ref={setActivatorNodeRef}
      {...attributes}
      {...listeners}
      data-testid={`sidebar-drag-handle-${id}`}
      className={cn(
        isMobile
          ? "flex size-5 shrink-0 cursor-grab touch-none items-center justify-center rounded-sm text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          : "pointer-events-auto flex size-5 shrink-0 cursor-grab touch-none items-center justify-center rounded-sm text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        isMobile && "opacity-100",
        isDragging && "opacity-100",
      )}
    >
      <GripVertical className="size-3.5" aria-hidden="true" />
      <span className="sr-only">{t("sidebar.reorderPin")}</span>
    </button>
  );

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "relative min-w-0",
        isDragging && "z-10 opacity-80",
      )}
      data-testid={testId}
      data-dragging={isDragging || undefined}
    >
      <div
        className={cn(
          "group flex min-h-9 min-w-0 items-center rounded-md",
          !isMobile && "relative",
          className,
          isDragging && "bg-muted/80",
        )}
      >
        {dragHandle}
        {children}
        {isMobile ? (
          actions
        ) : (
          <div className={cn("pointer-events-none absolute inset-y-0 right-1 z-10 flex items-center gap-0.5", actionsOverlayClassName)}>
            {actions}
          </div>
        )}
      </div>
      {below}
    </div>
  );
}
